// Geometry for the curtain entrance, drawn in a 100x100 viewBox (stretched to the screen).
// `progress` goes from 0 (closed) to 1 (pulled aside and tied back).

type Point = [number, number];

export interface CurtainFold {
	d: string;
	variant: number;
}

export interface CurtainParts {
	folds: CurtainFold[];
	tiebacks: string[];
	tiebackOpacity: number;
}

const FOLDS_PER_SIDE = 7;
const CLOSED_INNER_X = 51;
const TIEBACK_Y = 62;
// where the inner edge of a left curtain sits when tied back, as [y, x]: gathered at the top,
// pinched by the tie-back, flaring out towards the hem
const TIED_PROFILE: Point[] = [
	[0, 19],
	[20, 20],
	[40, 18],
	[TIEBACK_Y, 13],
	[76, 19],
	[88, 27],
	[100, 32]
];

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const smooth = (t: number) => t * t * (3 - 2 * t);
const round = (value: number) => Math.round(value * 100) / 100;

const tiedX = (y: number) => {
	for (let i = 1; i < TIED_PROFILE.length; i++) {
		const [y1, x1] = TIED_PROFILE[i];
		if (y <= y1) {
			const [y0, x0] = TIED_PROFILE[i - 1];
			return x0 + (x1 - x0) * smooth((y - y0) / (y1 - y0));
		}
	}
	return TIED_PROFILE[TIED_PROFILE.length - 1][1];
};

// the top of the curtain starts moving first, so mid-animation the inner edge sweeps in a diagonal
const innerX = (y: number, progress: number) => {
	const local = smooth(clamp(progress * 1.4 - 0.4 * (y / 100)));
	return CLOSED_INNER_X + (tiedX(y) - CLOSED_INNER_X) * local;
};

// x of fold boundary `k` (0 = outer edge) at height `y`, with a drape ripple once the curtain gathers
const boundaryX = (k: number, y: number, progress: number) => {
	const inner = innerX(y, progress);
	const ripple = k === 0 || k === FOLDS_PER_SIDE ? 0 : Math.sin(y * 0.09 + k * 1.7) * 0.7 * smooth(progress);
	return -2 + (inner + 2) * (k / FOLDS_PER_SIDE) + ripple;
};

const hemY = (k: number) => 95.5 + (k % 2 === 0 ? 2.2 : 0);

export const getCurtainParts = (progress: number): CurtainParts => {
	const folds: CurtainFold[] = [];
	const ys = Array.from({ length: 20 }, (_, i) => i * 5);

	([false, true] as const).forEach(mirror => {
		const px = (x: number) => round(mirror ? 100 - x : x);
		for (let k = 0; k < FOLDS_PER_SIDE; k++) {
			const hemA = hemY(k);
			const hemB = hemY(k + 1);
			const down = ys.filter(y => y < hemB).map(y => `L${px(boundaryX(k + 1, y, progress))} ${y}`);
			const up = ys
				.filter(y => y < hemA)
				.reverse()
				.map(y => `L${px(boundaryX(k, y, progress))} ${y}`);
			const xb = boundaryX(k + 1, hemB, progress);
			const xa = boundaryX(k, hemA, progress);
			const d =
				`M${px(boundaryX(k, 0, progress))} 0 L${px(boundaryX(k + 1, 0, progress))} 0 ` +
				`${down.join(" ")} L${px(xb)} ${hemB} ` +
				`Q${px((xa + xb) / 2)} ${round(Math.max(hemA, hemB) + 4.5)} ${px(xa)} ${hemA} ` +
				`${up.join(" ")} Z`;
			folds.push({ d, variant: k % 2 });
		}
	});

	const tiebacks = [false, true].map(mirror => {
		const px = (x: number) => round(mirror ? 100 - x : x);
		const inner = innerX(TIEBACK_Y, 1);
		return `M${px(-2)} ${TIEBACK_Y - 3} Q${px(inner / 2)} ${TIEBACK_Y + 4} ${px(inner + 1.5)} ${TIEBACK_Y}`;
	});

	return { folds, tiebacks, tiebackOpacity: smooth(clamp((progress - 0.75) / 0.25)) };
};
